import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "panel", "results", "prevButton", "nextButton", "closeButton"]
  static values = { currentIndex: Number, totalMatches: Number, conversationId: Number, searchUrl: String }

  currentIndexValueChanged() {
    this.updateResults()
    this.updateButtons()
  }

  totalMatchesValueChanged() {
    this.updateResults()
    this.updateButtons()
  }

  connect() {
    this.currentIndexValue = -1
    this.totalMatchesValue = 0
    this.matches = []
    this.highlightClass = "bg-yellow-300"
    this.messageBodySelector = ".message-body"
    this.loadedMessageIds = new Set()
    this.updateLoadedMessageIds()
  }

  updateLoadedMessageIds() {
    const messagesContainer = document.getElementById("messages")
    if (!messagesContainer) return

    const messages = messagesContainer.querySelectorAll("div[id^='message_']")
    messages.forEach((msg) => {
      const id = msg.id.replace("message_", "")
      this.loadedMessageIds.add(id)
    })
  }

  toggle() {
    if (this.panelTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.panelTarget.classList.remove("hidden")
    this.inputTarget.focus()
  }

  close() {
    this.panelTarget.classList.add("hidden")
    this.clearSearch()
    this.inputTarget.value = ""
  }

  async search() {
    const query = this.inputTarget.value.trim()

    if (query.length === 0) {
      this.clearSearch()
      return
    }

    this.clearHighlights()
    this.matches = []

    if (!this.conversationIdValue) return

    try {
      const response = await fetch(`${this.searchUrlValue}?query=${encodeURIComponent(query)}`, {
        headers: { "Accept": "application/json" }
      })
      const data = await response.json()
      const matchingIds = data.message_ids || []

      await this.loadMissingMessages(matchingIds)

      this.updateLoadedMessageIds()

      const regex = new RegExp(this.escapeRegex(query), "gi")

      const messagesContainer = document.getElementById("messages")
      if (!messagesContainer) return

      matchingIds.forEach((messageId) => {
        const messageEl = messagesContainer.querySelector(`#message_${messageId}`)
        if (!messageEl) return

        const bodyEl = messageEl.querySelector(this.messageBodySelector)
        if (!bodyEl) return

        this.matches.push(messageEl)
        this.highlightText(bodyEl, query, regex)
      })

      this.totalMatchesValue = this.matches.length
      this.currentIndexValue = this.matches.length > 0 ? 0 : -1

      if (this.matches.length > 0) {
        setTimeout(() => this.scrollToMatch(0), 100)
      }
    } catch (error) {
      console.error("Search error:", error)
    }
  }

  async loadMissingMessages(messageIds) {
    const missingIds = messageIds.filter(id => !this.loadedMessageIds.has(id.toString()))

    if (missingIds.length === 0) return

    if (!this.conversationIdValue) return

    const messagesContainer = document.getElementById("messages")
    if (!messagesContainer) return

    try {
      const response = await fetch(`${this.searchUrlValue}?message_ids=${missingIds.join(",")}`, {
        headers: {
          "Accept": "text/html"
        }
      })

      if (response.ok) {
        const html = await response.text()
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, "text/html")
        const messageElements = doc.querySelectorAll("div[id^='message_']")

        const allMessages = Array.from(messagesContainer.children)
        const messageMap = new Map()
        allMessages.forEach(msg => {
          const id = parseInt(msg.id.replace("message_", ""))
          messageMap.set(id, msg)
        })

        const sortedElements = Array.from(messageElements).sort((a, b) => {
          const idA = parseInt(a.id.replace("message_", ""))
          const idB = parseInt(b.id.replace("message_", ""))
          return idA - idB
        })

        sortedElements.forEach((messageEl) => {
          const messageId = parseInt(messageEl.id.replace("message_", ""))

          if (!messageMap.has(messageId)) {
            let insertBefore = null

            for (const existingMsg of allMessages) {
              const currentId = parseInt(existingMsg.id.replace("message_", ""))
              if (currentId > messageId) {
                insertBefore = existingMsg
                break
              }
            }

            if (insertBefore) {
              messagesContainer.insertBefore(messageEl, insertBefore)
            } else {
              messagesContainer.appendChild(messageEl)
            }

            messageMap.set(messageId, messageEl)
          }
        })
      }
    } catch (error) {
      console.error("Error loading messages:", error)
    }
  }

  highlightText(element, query, regex) {
    const text = element.textContent
    element.innerHTML = ""

    let lastIndex = 0
    let match

    regex.lastIndex = 0

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        element.appendChild(document.createTextNode(text.substring(lastIndex, match.index)))
      }

      const mark = document.createElement("mark")
      mark.className = `${this.highlightClass} px-0.5 rounded`
      mark.textContent = match[0]
      element.appendChild(mark)

      lastIndex = regex.lastIndex
    }

    if (lastIndex < text.length) {
      element.appendChild(document.createTextNode(text.substring(lastIndex)))
    }
  }

  clearHighlights() {
    const messagesContainer = document.getElementById("messages")
    if (!messagesContainer) return

    const messages = messagesContainer.querySelectorAll("div[id^='message_']")
    messages.forEach((messageEl) => {
      const bodyEl = messageEl.querySelector(this.messageBodySelector)
      if (bodyEl) {
        const text = bodyEl.textContent
        bodyEl.textContent = text
      }
    })
  }

  clearSearch() {
    this.clearHighlights()
    this.matches = []
    this.currentIndexValue = -1
    this.totalMatchesValue = 0
  }

  next() {
    if (this.matches.length === 0) return

    this.currentIndexValue = (this.currentIndexValue + 1) % this.matches.length
    this.scrollToMatch(this.currentIndexValue)
  }

  prev() {
    if (this.matches.length === 0) return

    this.currentIndexValue = this.currentIndexValue <= 0
      ? this.matches.length - 1
      : this.currentIndexValue - 1
    this.scrollToMatch(this.currentIndexValue)
  }

  scrollToMatch(index) {
    if (index < 0 || index >= this.matches.length) return

    const messageEl = this.matches[index]
    const container = document.getElementById("messages_container")

    if (messageEl && container) {
      const containerRect = container.getBoundingClientRect()
      const messageRect = messageEl.getBoundingClientRect()
      const containerScrollTop = container.scrollTop

      const messageTopRelativeToContainer = messageRect.top - containerRect.top + containerScrollTop
      const containerHeight = container.clientHeight
      const messageHeight = messageRect.height

      const targetScrollTop = messageTopRelativeToContainer - (containerHeight / 2) + (messageHeight / 2)

      container.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: "smooth"
      })

      setTimeout(() => {
        messageEl.classList.add("ring-2", "ring-blue-500", "ring-offset-2", "rounded-lg")
        setTimeout(() => {
          messageEl.classList.remove("ring-2", "ring-blue-500", "ring-offset-2", "rounded-lg")
        }, 2000)
      }, 300)
    }
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  }

  updateResults() {
    if (this.totalMatchesValue > 0) {
      this.resultsTarget.textContent = `${this.currentIndexValue + 1} / ${this.totalMatchesValue}`
    } else {
      this.resultsTarget.textContent = ""
    }
  }

  updateButtons() {
    const hasMatches = this.totalMatchesValue > 0
    this.prevButtonTarget.disabled = !hasMatches
    this.nextButtonTarget.disabled = !hasMatches
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.close()
    } else if (event.key === "Enter" && event.shiftKey === false) {
      event.preventDefault()
      if (event.ctrlKey || event.metaKey) {
        this.next()
      } else {
        this.search()
      }
    } else if (event.key === "ArrowDown" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      this.next()
    } else if (event.key === "ArrowUp" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault()
      this.prev()
    }
  }
}